import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { ComboboxSelfLoading } from '../../atoms/Combobox';
import { gql, useQuery } from '@apollo/client';
import CopyButton from '../../atoms/CopyButton';
import BarnCreator from './BarnCreator';
import CollapsibleSection from '../../atoms/CollapsibleSection';
import BarnBlock from '../../organisms/BarnBlock';
import './WorkshopPage';

const getOrgInfoQuery = gql`
  query OrgViewer_getInfo($id: uuid = "") {
    organization_by_pk(id: $id) {
      id
      name
      user_organizations_aggregate(where: { deleted_at: { _is_null: true } }) {
        aggregate {
          count
        }
      }
      farms_aggregate(where: { deleted_at: { _is_null: true } }) {
        aggregate {
          count
        }
      }
      user_organizations(order_by: [{ user: { name: asc_nulls_last } }, { user: { email: asc_nulls_last } }]) {
        deleted_at
        user {
          id
          name
          email
        }
      }

      barns: farms(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
        id
        name
        address
        feed_lines(where: { deleted_at: { _is_null: true } }) {
          name
          device_assignments(
            where: { deleted_at: { _is_null: true }, device: { deleted_at: { _is_null: true } } }
            order_by: { ended_at: desc_nulls_first }
          ) {
            started_at
            ended_at
            device {
              serial
            }
          }
        }
        user_barns(
          where: { deleted_at: { _is_null: true } }
          order_by: [{ user: { name: asc_nulls_last } }, { user: { email: asc_nulls_last } }]
        ) {
          user {
            id
            name
            email
          }
        }
      }
    }
  }
`;
export default function OrgManagement({ wrapperClassName = '', orgObject = null }) {
  const [org, setOrg] = useState();
  const { loading, data, error, refetch } = useQuery(getOrgInfoQuery, {
    variables: {
      id: org?.id,
    },
    skip: !org?.id,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setOrg(orgObject);
  }, [orgObject]);

  if (error) {
    throw error;
  }

  const orgInfo = data?.organization_by_pk;
  const barns = orgInfo?.barns;
  const farmsWithUsers = orgInfo?.barns.filter((b) => b.user_barns.length > 0);
  const nonDeletedOrgUserCount = orgInfo?.user_organizations?.filter((u) => !u.deleted_at).length;
  const userBarnsByUser = _(farmsWithUsers) // _(value) allows for chaining
    .flatMap((farm) =>
      farm.user_barns.map((ub) => {
        // invert relationship via the map
        return {
          userId: ub.user.id,
          userName: ub.user.name,
          userEmail: ub.user.email,
          farm: {
            id: farm.id,
            name: farm.name,
            address: farm.address,
          },
        };
      }),
    )
    .groupBy('userId')
    .map((entries, userId) => ({
      id: userId,
      name: entries[0].userName,
      email: entries[0].userEmail,
      barns: entries.map((entry) => entry.farm),
    }))
    .value();

  const flattenedDeviceAssignmentChain = _.chain(barns)
    .flatMap((barn) =>
      _.flatMap(barn.feed_lines, (feed_line) =>
        _.map(feed_line.device_assignments, (assignment) => ({
          id: assignment.id,
          barn_name: barn.name,
          feed_line_name: feed_line.name,
          started_at: assignment.started_at,
          ended_at: assignment.ended_at || Number.POSITIVE_INFINITY,
          device_serial: assignment.device?.serial || null,
        })),
      ),
    )
    .filter((da) => !da.device_serial.startsWith('MOCK')); // I don't feel this is useful info

  return (
    <div className={`OrgViewer ${wrapperClassName}`}>
      <br />
      <ComboboxSelfLoading
        fieldTableName="organization"
        placeholder="Select an org for the barn"
        name="org"
        label="Organization"
        preSelectedItem={org}
        onChange={(val) => {
          console.log('changed', val);
          setOrg(val);
        }}
      />
      <h1>Org: {orgInfo?.name}</h1>
      <p>
        <b>Org ID:</b>
        <span>{orgInfo?.id}</span> <CopyButton data={orgInfo?.id} />
      </p>
      <div>Total Barns {orgInfo?.farms_aggregate?.aggregate?.count}</div>
      <CollapsibleSection leftHeaderContent={'Create Barn Form'}>
        <BarnCreator orgId={org?.id} orgName={org?.name} refetch={refetch} />
      </CollapsibleSection>

      <CollapsibleSection isExpanded={true} title={<h2>Organization Users ({nonDeletedOrgUserCount})</h2>}>
        <div className="OrgViewer-OrgUsers">
          {orgInfo?.user_organizations?.map((uo, i) => {
            if (!uo?.user?.name) {
              console.log(uo);
            }
            return (
              <div className={`OrgViewer-orguser ${uo?.user?.deleted_at ? 'OrgViewer-orguser--deleted' : ''}`} key={i}>
                {uo?.user?.name ? `${uo?.user?.name} (${uo?.user?.email})` : uo?.user?.email}
                <span>
                  {uo?.user?.id}
                  <CopyButton data={uo?.user?.id} />
                </span>
              </div>
            );
          })}
        </div>
      </CollapsibleSection>
      {/* Flex looks ugly need better cards */}
      <CollapsibleSection
        title={
          <h2>
            Barn Access Users ({userBarnsByUser?.length} users, {farmsWithUsers?.length} barns)
          </h2>
        }
      >
        <div className="OrgViewer-PerBarnAccess" style={{ display: 'flex', flexDirection: 'column', rowGap: '1rem' }}>
          {userBarnsByUser?.length > 0 ? (
            userBarnsByUser.map((user, i) => {
              return (
                <div className="OrgViewer-PerBarnAccessCard" key={i}>
                  <h3>{user?.name ? `${user?.name} (${user?.email})` : user?.email}</h3>
                  <div className="OrgViewer-UserBarnWrapper" style={{ display: 'flex', columnGap: '1rem' }}>
                    {user?.barns?.map((barn, i) => {
                      return (
                        <span key={i}>
                          <a href={`/barns/${barn.id}`}>{barn.name}</a>
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <i>None</i>
          )}
        </div>
      </CollapsibleSection>
      <CollapsibleSection title={<h2>Device Histories</h2>}>
        <h3>
          Currently Installed Devices:{' '}
          {orgInfo
            ? flattenedDeviceAssignmentChain.value().filter((x) => x.ended_at === Number.POSITIVE_INFINITY)?.length
            : '?'}
        </h3>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            <h3>Sorted By Barn</h3>
            <table>
              <tr>
                <th>Barn</th>
                <th>Serial</th>
                <th>Feed Line</th>
                <th>Installed from</th>
                <th>Until</th>
              </tr>
              {flattenedDeviceAssignmentChain.value().map((d) => (
                <tr key={d.id}>
                  <td style={{ padding: '0.25rem' }}>{d.barn_name}</td>
                  <td style={{ padding: '0.25rem' }}>{d.device_serial}</td>
                  <td style={{ padding: '0.25rem' }}>{d.feed_line_name}</td>
                  <td style={{ padding: '0.25rem' }}>{dayjs.unix(d.started_at).format('YYYY-MMM-DD')}</td>
                  <td style={{ textAlign: 'center', padding: '0.25rem' }}>
                    {d.ended_at !== Number.POSITIVE_INFINITY ? dayjs.unix(d.ended_at).format('YYYY-MMM-DD') : 'Now'}
                  </td>
                </tr>
              ))}
            </table>
          </div>
          <div style={{ flex: 1 }}>
            <h3>Sorted By Install Time</h3>
            <table>
              <tr>
                <th>Installed from</th>
                <th>Until</th>
                <th>Barn</th>
                <th>Serial</th>
                <th>Feed Line</th>
              </tr>
              {flattenedDeviceAssignmentChain
                .orderBy(['started_at', 'device_serial'], ['desc', 'asc'])
                .value()
                .map((d) => (
                  <tr key={d.id}>
                    <td style={{ padding: '0.25rem' }}>{dayjs.unix(d.started_at).format('YYYY-MMM-DD hh:mm')}</td>
                    <td style={{ textAlign: 'center', padding: '0.25rem' }}>
                      {d.ended_at !== Number.POSITIVE_INFINITY ? dayjs.unix(d.ended_at).format('YYYY-MMM-DD') : 'Now'}
                    </td>
                    <td style={{ padding: '0.25rem' }}>{d.barn_name}</td>
                    <td style={{ padding: '0.25rem' }}>{d.device_serial}</td>
                    <td style={{ padding: '0.25rem' }}>{d.feed_line_name}</td>
                  </tr>
                ))}
            </table>
          </div>
          <div style={{ flex: 1 }}>
            {/* By Barn */}
            <h3>Sorted By Sensor Serial</h3>
            <table>
              <tr>
                <th>Serial</th>
                <th>Feed Line</th>
                <th>Barn</th>
                <th>Installed from</th>
                <th>Until</th>
              </tr>
              {flattenedDeviceAssignmentChain
                .orderBy(['device_serial', 'ended_at'], ['asc', 'desc'])
                .value()
                .map((d) => (
                  <tr key={d.id}>
                    <td style={{ padding: '0.25rem' }}>{d.device_serial}</td>
                    <td style={{ padding: '0.25rem' }}>{d.feed_line_name}</td>
                    <td style={{ padding: '0.25rem' }}>{d.barn_name}</td>
                    <td style={{ padding: '0.25rem' }}>{dayjs.unix(d.started_at).format('YYYY-MMM-DD')}</td>
                    <td style={{ textAlign: 'center', padding: '0.25rem' }}>
                      {d.ended_at !== Number.POSITIVE_INFINITY ? dayjs.unix(d.ended_at).format('YYYY-MMM-DD') : 'Now'}
                    </td>
                  </tr>
                ))}
            </table>
          </div>
        </div>
      </CollapsibleSection>
      <h2>All Org Barns ({barns?.length})</h2>
      <div className="OrgViewer-AllBarns">
        {barns?.length > 0 ? (
          barns.map((barn) => {
            const userCount = barn.user_barns?.length || 0;
            return (
              <div key={barn.id}>
                <BarnBlock
                  id={barn.id}
                  name={barn.name}
                  orgName={orgInfo.name}
                  address={barn.address}
                  className="OrgViewer-BarnBlock"
                  statLabel="Users with Direct Access"
                  statValue={userCount.toString()}
                  hasAlertPolicy={false}
                >
                  <CollapsibleSection title="Barn Users">
                    {barn?.user_barns?.map((ub) => {
                      return (
                        <div key={ub.id}>
                          {ub?.user?.name ? `${ub?.user?.name} (${ub?.user?.email})` : ub?.user?.email}
                        </div>
                      );
                    })}
                  </CollapsibleSection>
                  <CollapsibleSection title="Device History">
                    {barn?.feed_lines?.map((fl) => {
                      return (
                        <div key={fl.id} style={{ margin: '2rem', backgroundColor: 'lightgrey' }}>
                          <span>Feed line: </span>
                          <span>{fl.name}</span>
                          {fl?.device_assignments?.map((da) => {
                            return (
                              <div key={da.id} style={{ marginLeft: '0.5rem' }}>
                                <span>{da.serial}</span>
                                <span style={{ color: 'black' }}>
                                  {/* TODO NEED TO DO DATES BETTER AND VISUALIZE BETTER */}
                                  {dayjs.unix(da.started_at).format('YYYY-MMM-DD')} to{' '}
                                  {da.ended_at ? dayjs.unix(da.ended_at).format('YYYY-MMM-DD') : 'Now'}
                                  {/* <CopyButton data={da.ended_at} /> */}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </CollapsibleSection>
                </BarnBlock>
              </div>
            );
          })
        ) : (
          <i>None</i>
        )}
      </div>
    </div>
  );
}

OrgManagement.propTypes = {
  wrapperClassName: PropTypes.string,
  orgObject: PropTypes.object,
};
